import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { GlobalSearchResult } from '@kangaroo/ui';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import GarageOutlinedIcon from '@mui/icons-material/GarageOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { fetchDrivers } from '../drivers';
import { fetchKarts } from '../karts';
import { fetchDataFiles } from '../dataFiles';
import { fetchTeamMembers } from '../teams';
import { fetchTelemetryTracks } from '../telemetryContext';
import { useAuthStore } from '../../store/authStore';
import { createGlobalSearchService } from './service';
import { subscribeGlobalSearchInvalidation } from './invalidation';

const icons = {
  driver: <PersonOutlineOutlinedIcon />,
  member: <Groups2OutlinedIcon />,
  kart: <GarageOutlinedIcon />,
  session: <FlagOutlinedIcon />,
  circuit: <MapOutlinedIcon />,
};
const emptyResults: GlobalSearchResult[] = [];

export function useGlobalSearch(query: string, teamId: string) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const sessionKind = useAuthStore((state) => state.sessionKind);
  const service = useMemo(
    () =>
      createGlobalSearchService({
        fetchDrivers,
        fetchKarts,
        fetchDataFiles,
        fetchTeamMembers,
        fetchTelemetryTracks,
        isCurrent: () => {
          const current = useAuthStore.getState();
          return Boolean(
            token &&
            user?.id &&
            teamId &&
            current.token === token &&
            current.user === user &&
            current.user?.teamId === teamId &&
            current.sessionKind === sessionKind,
          );
        },
      }),
    [token, user, teamId, sessionKind],
  );
  const snapshot = useSyncExternalStore(
    service.subscribe,
    service.getSnapshot,
    service.getSnapshot,
  );

  useEffect(() => {
    const unsubscribe = subscribeGlobalSearchInvalidation((reason) => {
      if (reason === 'clear') service.clear();
      else service.invalidate();
    });
    return () => {
      unsubscribe();
      service.clear();
    };
  }, [service]);
  useEffect(() => {
    service.search(query);
  }, [service, query]);

  const results = useMemo(
    () =>
      snapshot.results.map(({ kind, ...result }) => ({
        ...result,
        icon: icons[kind],
      })),
    [snapshot.results],
  );
  // Hide old results during the render preceding the query effect.
  const matchesQuery = snapshot.query === query.trim();
  return {
    results: matchesQuery ? results : emptyResults,
    loading: matchesQuery && snapshot.loading,
  };
}
